/**
 * Template Processing Service V2
 * 
 * A simplified, focused approach to template processing that:
 * 1. Only sends the input fields selected in the template
 * 2. Only processes the output fields selected in the template
 * 3. Uses field-specific system prompts based on the output fields
 */
import { countProductImages, generatePromptHint } from './imagePlaceholderService';
import { getFieldValue as defaultGetFieldValue } from '../utils/templateUtils';

/**
 * Field-specific system prompts - minimal instructions focused on format
 */
const FIELD_PROMPTS = {
  // Common format instructions for all fields
  default: `You are a Shopify product content assistant. Respond ONLY with a valid JSON object containing the requested fields. Do not include any explanations or additional text outside of the JSON.`,
  
  // Basic product fields
  title: `Respond with a valid JSON object containing only the title field. Example: {"title": "Your generated title here"}`,
  body_html: `Respond with a valid JSON object containing only the body_html field with properly formatted HTML. Example: {"body_html": "<p>Your generated description here</p>"}`,
  vendor: `Respond with a valid JSON object containing only the vendor field. Example: {"vendor": "Brand Name"}`,
  product_type: `Respond with a valid JSON object containing only the product_type field. Example: {"product_type": "T-Shirt"}`,
  handle: `Respond with a valid JSON object containing only the handle field. Example: {"handle": "product-handle-slug"}`,
  
  // Tags
  tags: `Respond with a valid JSON object containing only the tags field as a comma-separated string. Example: {"tags": "tag1, tag2, tag3"}`,
  
  // Media fields - only include alt_text, remove other alt text related fields
  alt_text: `Respond with a valid JSON object containing only the alt_text field for the product's main image. Example: {"alt_text": "Description of the image"}`,
  
  // Options - IMPORTANT: Use exact field names as shown in examples
  option1_name: `Respond with a valid JSON object containing ONLY the option1_name field (exact field name). Example: {"option1_name": "Size"}`,
  option1_values: `Respond with a valid JSON object containing ONLY the option1_values field (exact field name) as an array. Example: {"option1_values": ["Small", "Medium", "Large"]}`,
  option2_name: `Respond with a valid JSON object containing ONLY the option2_name field (exact field name). Example: {"option2_name": "Color"}`,
  option2_values: `Respond with a valid JSON object containing ONLY the option2_values field (exact field name) as an array. Example: {"option2_values": ["Red", "Blue", "Green"]}`,
  option3_name: `Respond with a valid JSON object containing ONLY the option3_name field (exact field name). Example: {"option3_name": "Material"}`,
  option3_values: `Respond with a valid JSON object containing ONLY the option3_values field (exact field name) as an array. Example: {"option3_values": ["Cotton", "Polyester", "Wool"]}`,
  
  // Variants
  variants: `Respond with a valid JSON object containing only the variants field as an array. IMPORTANT: Each variant MUST include its ID. Example: {"variants": [{"id": 123, ...other fields}, {"id": 456, ...other fields}]}`,
  'variants.sku': `Respond with a valid JSON object containing variants with SKU values. IMPORTANT: Each variant MUST include its ID. Example: {"variants": [{"id": 123, "sku": "SKU-001"}, {"id": 456, "sku": "SKU-002"}]}`,
  'variants.price': `Respond with a valid JSON object containing variants with price values. IMPORTANT: Each variant MUST include its ID. Example: {"variants": [{"id": 123, "price": "19.99"}, {"id": 456, "price": "24.99"}]}`,
  'variants.compare_at_price': `Respond with a valid JSON object containing variants with compare_at_price values. IMPORTANT: Each variant MUST include its ID. Example: {"variants": [{"id": 123, "compare_at_price": "29.99"}, {"id": 456, "compare_at_price": "34.99"}]}`,
  'variants.inventory_quantity': `Respond with a valid JSON object containing variants with inventory_quantity values. IMPORTANT: Each variant MUST include its ID. Example: {"variants": [{"id": 123, "inventory_quantity": 10}, {"id": 456, "inventory_quantity": 20}]}`,
};

/**
 * Map alternative field names to standard field names
 * This helps handle variations in AI model responses
 * @param {string} field - The field name to map
 * @returns {string} The mapped field name
 */
export const mapResponseFieldName = (field) => {
  const fieldMappings = {
    // Title variations
    'seo_title': 'title',
    'product_title': 'title',
    'optimized_title': 'title',
    
    // Description variations
    'seo_description': 'body_html',
    'product_description': 'body_html',
    'optimized_description': 'body_html',
    'description': 'body_html',
    'content': 'body_html',
    'html': 'body_html',
    
    // Product type variations
    'ProductType': 'product_type',
    'productType': 'product_type',
    'product-type': 'product_type',
    'producttype': 'product_type',
    
    // Image alt text variations
    'imageAltText': 'alt_text',
    'image_alt_text': 'alt_text',
    'image_alt': 'alt_text',
    'alt': 'alt_text',
    
    // Option field variations - handle different formats from AI responses
    'Option1 Name': 'option1_name',
    'Option1 Values': 'option1_values',
    'Option2 Name': 'option2_name', 
    'Option2 Values': 'option2_values',
    'Option3 Name': 'option3_name',
    'Option3 Values': 'option3_values',
    'option1name': 'option1_name',
    'option1values': 'option1_values',
    'option2name': 'option2_name',
    'option2values': 'option2_values', 
    'option3name': 'option3_name',
    'option3values': 'option3_values',
    
    // Other common variations
    'tags_list': 'tags',
    'keywords': 'tags'
  };
  
  return fieldMappings[field] || field;
};

/**
 * Get a metafield prompt for a specific namespace and key
 * @param {string} namespace - The metafield namespace
 * @param {string} key - The metafield key
 * @returns {string} The metafield prompt
 */
const getMetafieldPrompt = (namespace, key) => {
  return `Respond with a valid JSON object containing only the metafield value. If the metafield contains HTML, ensure all quotes within HTML attributes are properly escaped with backslashes. Example: {"metafields": {"${namespace}.${key}": "Your generated metafield value here"}}`;
};

/**
 * Get a metafield description for a specific namespace and key
 * @param {string} namespace - The metafield namespace
 * @param {string} key - The metafield key
 * @returns {string} The metafield description
 */
const getMetafieldDescription = (namespace, key) => {
  return `a metafield value for "${namespace}.${key}"`;
};

/**
 * Get a field description for a specific field
 * @param {string} field - The field name
 * @returns {string} The field description
 */
const getFieldDescription = (field) => {
  return `a value for "${field}"`;
};

/**
 * Check if a field is a metafield (contains a dot)
 * @param {string} field - The field to check
 * @returns {boolean} True if the field is a metafield
 */
const isMetafield = (field) => {
  return field.startsWith('metafields.') || field.includes('.') && !field.startsWith('variants.');
};

/**
 * Parse a metafield path or object into its components
 * @param {string|Object} metafieldPath - The metafield path (e.g., "metafields.namespace.key" or "namespace.key") or object {namespace, key}
 * @returns {Object} The namespace and key
 */
const parseMetafieldPath = (metafieldPath) => {
  // Handle object format
  if (typeof metafieldPath === 'object' && metafieldPath !== null) {
    // If it's already an object with namespace and key, return it
    if (metafieldPath.namespace && metafieldPath.key) {
      return {
        namespace: metafieldPath.namespace,
        key: metafieldPath.key
      };
    }
    
    // Invalid object format
    console.error('%c[Invalid Metafield Object Format]', 'color: #cc3300; font-weight: bold', metafieldPath);
    return { namespace: 'custom', key: 'unknown' };
  }
  
  // Handle string format
  if (typeof metafieldPath === 'string') {
    // Handle both "metafields.namespace.key" and "namespace.key" formats
    const parts = metafieldPath.replace(/^metafields\./, '').split('.');
    
    if (parts.length < 2) {
      return { namespace: 'custom', key: parts[0] };
    }
    
    return {
      namespace: parts[0],
      key: parts[1]
    };
  }
  
  // Invalid format
  console.error('%c[Invalid Metafield Format]', 'color: #cc6600; font-weight: bold', {
    metafieldPath,
    type: typeof metafieldPath
  });
  
  return { namespace: 'custom', key: 'unknown' };
};

/**
 * Get the appropriate system prompt based on output fields
 * @param {Array} outputFields - The output fields requested in the template
 * @param {Array} outputMetafields - Optional array of output metafields
 * @returns {string} The system prompt
 */
export const getSystemPromptForFields = (outputFields, outputMetafields = []) => {
  // Combine regular fields and metafields
  const allOutputFields = [...(outputFields || [])];
  
  // Add metafields from outputMetafields array (objects with namespace and key)
  if (outputMetafields && outputMetafields.length > 0) {
    outputMetafields.forEach(metafield => {
      if (metafield && metafield.namespace && metafield.key) {
        allOutputFields.push(`metafields.${metafield.namespace}.${metafield.key}`);
      }
    });
  }
  
  // If no output fields, use default prompt
  if (!allOutputFields || allOutputFields.length === 0) {
    return FIELD_PROMPTS.default;
  }
  
  // If there's only one output field, use its specific prompt
  if (allOutputFields.length === 1) {
    const field = allOutputFields[0];
    
    // Handle metafields
    if (isMetafield(field)) {
      const { namespace, key } = parseMetafieldPath(field);
      return getMetafieldPrompt(namespace, key);
    }
    
    return FIELD_PROMPTS[field] || FIELD_PROMPTS.default;
  }
  
  // For multiple fields, build a custom prompt
  let prompt = 'You are a helpful assistant that generates content for Shopify products.\n\n';
  prompt += 'CRITICAL: You MUST respond with a JSON object using the EXACT field names listed below. Do NOT translate or modify the field names.\n\n';
  prompt += 'Required JSON format:\n{\n';
  
  // Handle variant fields specially to group them into a single array
  const variantFields = [];
  const normalFields = [];
  
  allOutputFields.forEach(field => {
    if (field === 'variants' || field.startsWith('variants.')) {
      variantFields.push(field);
    } else {
      normalFields.push(field);
    }
  });

  const totalSegments = normalFields.length + (variantFields.length > 0 ? 1 : 0);
  let currentSegment = 0;

  // Add normal fields to the JSON example
  normalFields.forEach(field => {
    currentSegment++;
    const isLast = currentSegment === totalSegments;
    if (isMetafield(field)) {
      const { namespace, key } = parseMetafieldPath(field);
      prompt += `  "${namespace}.${key}": "your generated value"${isLast ? '' : ','}\n`;
    } else {
      if (field.includes('option') && field.includes('_name')) {
        prompt += `  "${field}": "translated option name"${isLast ? '' : ','}\n`;
      } else if (field.includes('option') && field.includes('_values')) {
        prompt += `  "${field}": "value1, value2, value3"${isLast ? '' : ','}\n`;
      } else {
        prompt += `  "${field}": "your generated value"${isLast ? '' : ','}\n`;
      }
    }
  });

  // Add variants group if needed
  if (variantFields.length > 0) {
    currentSegment++;
    const isLast = currentSegment === totalSegments;
    prompt += `  "variants": [\n    {\n      "id": 123456`;
    
    // Add each requested variant field
    const uniqueVariantAttrs = new Set();
    variantFields.forEach(vf => {
      if (vf === 'variants') {
        uniqueVariantAttrs.add('sku');
        uniqueVariantAttrs.add('price');
      } else {
        uniqueVariantAttrs.add(vf.split('.')[1]);
      }
    });

    uniqueVariantAttrs.forEach(attr => {
      prompt += `,\n      "${attr}": "generated value"`;
    });
    
    prompt += `\n    }\n  ]${isLast ? '' : ','}\n`;
  }

  prompt += '}\n\n';
  prompt += 'Field descriptions:\n';
  
  // Add field descriptions
  allOutputFields.forEach(field => {
    if (isMetafield(field)) {
      const { namespace, key } = parseMetafieldPath(field);
      prompt += `- "${namespace}.${key}": ${getMetafieldDescription(namespace, key)}\n`;
    } else {
      if (field.includes('option') && field.includes('_name')) {
        prompt += `- "${field}": Translated option name (e.g., "Size", "Color")\n`;
      } else if (field.includes('option') && field.includes('_values')) {
        prompt += `- "${field}": Translated option values as comma-separated string in the EXACT SAME ORDER as the original values (e.g., "Small, Medium, Large")\n`;
      } else {
        prompt += `- "${field}": ${getFieldDescription(field)}\n`;
      }
    }
  });
  
  // Add instructions for metafields
  if (outputMetafields && outputMetafields.length > 0) {
    prompt += '\nIMPORTANT: For metafields, you MUST use the EXACT field name format "namespace.key" in your response. ';
    prompt += 'For example, use "custom.product_specifications" NOT "specifications" or "product_specifications".\n';
    
    // List all expected metafield keys
    prompt += '\nExpected metafield keys in your response:\n';
    outputMetafields.forEach(metafield => {
      if (metafield && metafield.namespace && metafield.key) {
        prompt += `- "${metafield.namespace}.${metafield.key}"\n`;
      }
    });
  }
  
  prompt += '\nCRITICAL INSTRUCTIONS:\n';
  prompt += '- Respond with valid JSON only\n';
  prompt += '- Use the EXACT field names shown in the JSON format above\n';
  prompt += '- Do NOT translate field names like "option1_name" to "Lamp color"\n';
  prompt += '- Do NOT add explanations or extra text\n';
  prompt += '- The field names are case-sensitive and must match exactly\n';
  prompt += '- IMPORTANT: For option values, maintain the EXACT SAME ORDER as the original values to preserve image associations\n';
  prompt += '- If original values are "Orange, Grey", translated values must be in same order: "Orange (translated), Grey (translated)"';
  
  return prompt;
};

/**
 * Process the template result and extract the output fields
 * @param {Object} result - The parsed OpenAI result
 * @param {Object} product - The product object
 * @param {Object} template - The template configuration
 * @returns {Object} The processed result with only the output fields
 */
export const processTemplateResult = (result, product, template) => {
  try {
    // Log the raw template result
    console.log('%c[Raw Template Result]', 'color: #ff6600; font-weight: bold; font-size: 14px');
    console.log(JSON.stringify(result, null, 2));
    
    console.log('%c[Processing Template Result]', 'color: #339966; font-weight: bold', {
      templateId: template.id,
      templateName: template.name,
      outputFields: template.outputFields || [],
      outputMetafields: template.outputMetafields || [],
      resultKeys: Object.keys(result),
      hasMetafields: !!result.metafields,
      metafieldKeys: result.metafields ? Object.keys(result.metafields) : []
    });
    
    // Process variant SKUs if the output includes SKU field
    if (template.outputFields && (template.outputFields.includes('sku') || template.outputFields.includes('variants.sku'))) {
      result = processVariantSKUs(result, product, template);
    }
    
    // Initialize the processed result
    const processedResult = {};
    
    // Get the output fields from the template
    const outputFields = template.outputFields || [];
    const outputMetafields = template.outputMetafields || [];
    
    // Create a filtered result with only the requested fields
    const filteredResult = {};
    
    // Map alternative field names in the result to standard field names
    const mappedResult = {};
    Object.keys(result).forEach(key => {
      const mappedKey = mapResponseFieldName(key);
      mappedResult[mappedKey] = result[key];
      
      if (mappedKey !== key) {
        console.log(`Mapped response field '${key}' to standard field '${mappedKey}'`);
      }
    });
    
    // Combine original result with mapped result, with mapped fields taking precedence
    const enhancedResult = { ...result, ...mappedResult };
    
    // Process each output field
    outputFields.forEach(field => {
      try {
        // Get the exact Shopify field name
        const shopifyFieldName = getShopifyFieldName(field);
        
        // Check if the field exists in the result with the exact Shopify field name
        if (enhancedResult[shopifyFieldName] !== undefined) {
          processedResult[field] = enhancedResult[shopifyFieldName];
          return;
        }
        
        // Check for alternative field names that might contain our target field
        // For example, if we want 'handle' but the AI returned 'generated_handle'
        const possibleAlternatives = Object.keys(enhancedResult).filter(key => 
          key.includes(shopifyFieldName) || 
          key.includes(field) ||
          (field === 'handle' && (key.includes('handle') || key.includes('slug'))) ||
          (field === 'product_type' && (key.toLowerCase().includes('type') || key.toLowerCase().includes('category'))) ||
          (field === 'alt_text' && (key.toLowerCase().includes('alt') || key.toLowerCase().includes('image')))
        );
        
        if (possibleAlternatives.length > 0) {
          // Use the first alternative that contains our field name
          const alternativeField = possibleAlternatives[0];
          processedResult[field] = enhancedResult[alternativeField];
          
          console.log(`%c[Using Alternative Field]`, 'color: #cc9900; font-weight: bold', {
            requestedField: field,
            shopifyFieldName,
            alternativeField,
            value: enhancedResult[alternativeField]
          });
          return;
        }
        
        // Handle nested fields (e.g., variants.sku)
        if (field.includes('.')) {
          const [parent, child] = field.split('.');
          
          // Handle variants specially
          if (parent === 'variants') {
            if (enhancedResult.variants && Array.isArray(enhancedResult.variants)) {
              // Check if we have a product to match variants with
              if (template.product && template.product.variants) {
                console.log('%c[Processing Variants with Product]', 'color: #669933; font-weight: bold', {
                  templateType: template.type,
                  generatedVariants: enhancedResult.variants.length,
                  productVariantCount: template.product.variants.length
                });
                
                // Ensure variant IDs match the product variants
                const processedVariants = ensureVariantIds(enhancedResult.variants, template.product);
                
                // If this is a variant template, we need to be extra careful with the IDs
                if (template.type === 'variant') {
                  console.log('%c[Variant Template Processing]', 'color: #669933; font-weight: bold', {
                    variantCount: processedVariants.length,
                    field: child
                  });
                  
                  // For variant templates, we only want to include variants that have valid IDs
                  // and match the product variants
                  const validVariants = processedVariants.filter(variant => {
                    if (!variant.id) return false;
                    
                    // Check if this variant ID exists in the product
                    const matchingVariant = template.product.variants.find(
                      v => v.id.toString() === variant.id.toString()
                    );
                    
                    return !!matchingVariant;
                  });
                  
                  if (validVariants.length < processedVariants.length) {
                    console.warn('%c[Filtered Invalid Variants]', 'color: #cc9900; font-weight: bold', {
                      original: processedVariants.length,
                      valid: validVariants.length,
                      removed: processedVariants.length - validVariants.length
                    });
                  }
                  
                  // If we're only interested in a specific variant field (e.g., sku)
                  if (child && child !== '*') {
                    // Create a variants array with only the ID and the requested field
                    processedResult.variants = validVariants.map(variant => ({
                      id: variant.id,
                      [child]: variant[child]
                    }));
                  } else {
                    // Include all variant data
                    processedResult.variants = validVariants;
                  }
                } else {
                  // For non-variant templates, just include all variants
                  if (child && child !== '*') {
                    // Create a variants array with only the ID and the requested field
                    processedResult.variants = processedVariants.map(variant => ({
                      id: variant.id,
                      [child]: variant[child]
                    }));
                  } else {
                    // Include all variant data
                    processedResult.variants = processedVariants;
                  }
                }
              } else {
                // No product to match with, just use the variants as-is
                console.warn('%c[No Product for Variant Matching]', 'color: #cc9900; font-weight: bold', {
                  templateType: template.type,
                  field
                });
                
                if (child && child !== '*') {
                  // Create a variants array with only the ID and the requested field
                  processedResult.variants = enhancedResult.variants.map(variant => ({
                    id: variant.id || 'unknown',
                    [child]: variant[child]
                  }));
                } else {
                  // Include all variant data
                  processedResult.variants = enhancedResult.variants;
                }
              }
            }
          } else {
            // Handle other nested fields
            if (enhancedResult[parent] && enhancedResult[parent][child] !== undefined) {
              if (!processedResult[parent]) {
                processedResult[parent] = {};
              }
              processedResult[parent][child] = enhancedResult[parent][child];
            }
          }
        } else {
          // Handle top-level fields
          if (enhancedResult[field] !== undefined) {
            processedResult[field] = enhancedResult[field];
          } 
          // Check if the field exists in result.metafields
          else if (enhancedResult.metafields && enhancedResult.metafields[field] !== undefined) {
            processedResult[field] = enhancedResult.metafields[field];
            
            console.log('%c[Extracted Field from Metafields]', 'color: #9900cc; font-weight: bold', {
              field,
              value: processedResult[field]
            });
          }
        }
      } catch (error) {
        console.error('%c[Output Field Processing Error]', 'color: #cc3300; font-weight: bold', {
          field,
          error: error.message
        });
        // Continue with other fields
      }
    });
    
    // Process output metafields
    if (outputMetafields && Array.isArray(outputMetafields)) {
      outputMetafields.forEach(metafieldKey => {
        try {
          // Handle different metafield formats
          let namespace, key, value;
          
          // Check if metafieldKey is an object with namespace and key properties
          if (typeof metafieldKey === 'object' && metafieldKey !== null) {
            // Handle object format: { namespace: 'custom', key: 'googlekeywords' }
            namespace = metafieldKey.namespace;
            key = metafieldKey.key;
            
            console.log('%c[Processing Output Metafield (Object)]', 'color: #669966; font-weight: bold', {
              metafieldKey: `${namespace}.${key}`,
              namespace,
              key
            });
          } else if (typeof metafieldKey === 'string' && metafieldKey.includes('.')) {
            // Handle string format: 'namespace.key'
            [namespace, key] = metafieldKey.split('.');
            
            console.log('%c[Processing Output Metafield (String)]', 'color: #669966; font-weight: bold', {
              metafieldKey,
              namespace,
              key
            });
          } else {
            // Invalid format
            console.error('%c[Output Metafield Invalid Format]', 'color: #cc6600; font-weight: bold', {
              metafieldKey,
              type: typeof metafieldKey
            });
            return; // Skip this metafield
          }
          
          // Create the full metafield key
          const fullKey = `${namespace}.${key}`;
          
          let metafieldValue = undefined;
          let foundMethod = '';
          
          // Debug: Log what we're looking for and what's available
          console.log('%c[DEBUG: Metafield Search]', 'color: #ff0000; font-weight: bold');
          console.log('Looking for:', fullKey);
          console.log('Available in enhancedResult:', Object.keys(enhancedResult));
          console.log('enhancedResult.metafields exists:', !!enhancedResult.metafields);
          if (enhancedResult.metafields) {
            console.log('Available in metafields:', Object.keys(enhancedResult.metafields));
          }
          
          // Method 1: Check if the AI returned the metafield in the nested metafields object
          if (enhancedResult.metafields && enhancedResult.metafields[fullKey] !== undefined) {
            metafieldValue = enhancedResult.metafields[fullKey];
            foundMethod = 'Nested (metafields object)';
            console.log('%c[DEBUG: Found via Method 1]', 'color: #00ff00; font-weight: bold', metafieldValue);
          }
          // Method 2: Check if the AI returned the full metafield key directly
          else if (enhancedResult[fullKey] !== undefined) {
            metafieldValue = enhancedResult[fullKey];
            foundMethod = 'Direct (root level)';
            console.log('%c[DEBUG: Found via Method 2]', 'color: #00ff00; font-weight: bold', metafieldValue);
          }
          // Method 3: Check if the AI returned it under just the key name (without namespace)
          else if (enhancedResult[key] !== undefined) {
            metafieldValue = enhancedResult[key];
            foundMethod = 'Key only (without namespace)';
            console.log('%c[DEBUG: Found via Method 3]', 'color: #00ff00; font-weight: bold', metafieldValue);
          }
          else {
            console.log('%c[DEBUG: Metafield NOT FOUND in any method]', 'color: #ff0000; font-weight: bold');
          }
          
          // If we found the metafield value, process it
          if (metafieldValue !== undefined) {
            // Ensure metafields exists in processedResult
            if (!processedResult.metafields) {
              processedResult.metafields = {};
            }
            
            // Add the value
            processedResult.metafields[fullKey] = metafieldValue;
            
            console.log('%c[Processed Output Metafield]', 'color: #669966; font-weight: bold', {
              metafieldKey: fullKey,
              namespace,
              key,
              foundMethod,
              valueType: typeof metafieldValue,
              valueLength: typeof metafieldValue === 'string' ? metafieldValue.length : null
            });
          } else {
            console.log('%c[Metafield Not Found in AI Response]', 'color: #cc6600; font-weight: bold', {
              metafieldKey: fullKey,
              availableKeys: Object.keys(enhancedResult),
              hasMetafields: !!enhancedResult.metafields,
              metafieldKeys: enhancedResult.metafields ? Object.keys(enhancedResult.metafields) : []
            });
            
            // Additional debugging - log the full enhanced result structure
            console.log('%c[DEBUG: Full Enhanced Result Structure]', 'color: #ff0000; font-weight: bold');
            console.log('enhancedResult:', JSON.stringify(enhancedResult, null, 2));
            
            // Check if metafields exists and what it contains
            if (enhancedResult.metafields) {
              console.log('%c[DEBUG: Metafields Object]', 'color: #ff0000; font-weight: bold');
              console.log('enhancedResult.metafields:', JSON.stringify(enhancedResult.metafields, null, 2));
              console.log('Looking for key:', fullKey);
              console.log('Available metafield keys:', Object.keys(enhancedResult.metafields));
              console.log('Direct access test:', enhancedResult.metafields[fullKey]);
            }
          }
        } catch (error) {
          console.error('%c[Output Metafield Processing Error]', 'color: #cc6600; font-weight: bold', {
            metafieldKey: typeof metafieldKey === 'object' ? 
              `${metafieldKey?.namespace || 'unknown'}.${metafieldKey?.key || 'unknown'}` : 
              String(metafieldKey),
            error: error.message
          });
          // Continue with other metafields
        }
      });
    }
    
    // Add variant_skus to the processed result if present
    if (result.variant_skus) {
      processedResult.variant_skus = result.variant_skus;
      console.log('%c[Added Variant SKUs to Result]', 'color: #6600cc; font-weight: bold', {
        variantCount: Object.keys(result.variant_skus).length
      });
    }
    
    console.log('%c[Template Result Processing Complete]', 'color: #339966; font-weight: bold', {
      fieldCount: Object.keys(processedResult).filter(k => k !== 'metafields' && k !== 'variant_skus').length,
      metafieldCount: processedResult.metafields ? Object.keys(processedResult.metafields).length : 0
    });
    
    return processedResult;
  } catch (error) {
    console.error('%c[Template Result Processing Error]', 'color: #cc3300; font-weight: bold', {
      error: error.message
    });
    throw new Error(`Error processing template result: ${error.message}`);
  }
};

/**
 * Process template results for variants when the output includes SKU field
 * This function generates unique SKUs for each variant based on the template result
 * @param {Object} result - The parsed AI result
 * @param {Object} product - The product object
 * @param {Object} template - The template configuration
 * @returns {Object} The processed result with variant SKUs if applicable
 */
export const processVariantSKUs = (result, product, template) => {
  // Check if we're generating SKUs and have variants
  if (!result.sku || !product.variants || !product.variants.length) {
    return result;
  }

  console.log('%c[Processing Variant SKUs]', 'color: #6600cc; font-weight: bold', {
    productId: product.id,
    variantCount: product.variants.length,
    baseSKU: result.sku
  });

  // Get option names
  let option1Name = '';
  let option2Name = '';
  let option3Name = '';
  
  if (product.options && Array.isArray(product.options)) {
    const option1 = product.options.find(o => o.position === 1 || o.name.toLowerCase().includes('color') || o.name.toLowerCase().includes('colour'));
    const option2 = product.options.find(o => o.position === 2 || o.name.toLowerCase().includes('size'));
    const option3 = product.options.find(o => o.position === 3 || o.name.toLowerCase().includes('style') || o.name.toLowerCase().includes('material'));
    
    option1Name = option1 ? option1.name : '';
    option2Name = option2 ? option2.name : '';
    option3Name = option3 ? option3.name : '';
  }
  
  console.log('%c[Product Options]', 'color: #6600cc; font-weight: bold', {
    option1Name,
    option2Name,
    option3Name
  });

  // Create a result with variant SKUs
  const variantResults = {};

  // Process each variant
  product.variants.forEach(variant => {
    // Helper function to get current option value from UI or original data
    const getCurrentOptionValue = (optionIndex, originalValue) => {
      // First try to find the translated value by looking up the original value in the option values array
      if (typeof window !== 'undefined' && window.localEdits && window.localEdits[product.id]) {
        const optionValuesField = `option${optionIndex}_values`;
        if (window.localEdits[product.id][optionValuesField]) {
          // Get the translated option values array
          const translatedValues = window.localEdits[product.id][optionValuesField];
          
          // Convert string to array if needed
          let translatedValuesArray = translatedValues;
          if (typeof translatedValues === 'string') {
            translatedValuesArray = translatedValues.split(',').map(v => v.trim());
          }
          
          // Try to find the original value in the product's options and map to translated
          if (Array.isArray(translatedValuesArray)) {
            // Find the index of the original value in the original product options
            if (product && product.options) {
              const option = product.options.find(opt => opt.position === optionIndex);
              if (option && option.values) {
                const originalIndex = option.values.findIndex(val => val === originalValue);
                
                if (originalIndex !== -1 && translatedValuesArray[originalIndex]) {
                  return translatedValuesArray[originalIndex];
                }
              }
            }
          }
        }
      }
      
      // Fallback: try the specific field name approach
      const fieldName = `option${optionIndex}_value_${variant.position - 1}`;
      
      // Check window.localEdits if available
      if (typeof window !== 'undefined' && window.localEdits && window.localEdits[product.id]) {
        if (window.localEdits[product.id][fieldName]) {
          return window.localEdits[product.id][fieldName];
        }
      }
      
      // Fall back to original value
      return originalValue;
    };

    // Get option values for this variant - check for updated values first
    let option1Value = getCurrentOptionValue(1, variant.option1) || '';
    let option2Value = getCurrentOptionValue(2, variant.option2) || '';
    let option3Value = getCurrentOptionValue(3, variant.option3) || '';
    
    // If not available, try to get from selectedOptions
    if ((!option1Value || option1Value === 'Default Title') && variant.selectedOptions && variant.selectedOptions.length > 0) {
      const opt1 = variant.selectedOptions.find(o => o.name === option1Name || 
                                               o.name.toLowerCase().includes('color') || 
                                               o.name.toLowerCase().includes('colour'));
      if (opt1) {
        option1Value = getCurrentOptionValue(1, opt1.value) || opt1.value;
      }
    }
    
    if (!option2Value && variant.selectedOptions && variant.selectedOptions.length > 0) {
      const opt2 = variant.selectedOptions.find(o => o.name === option2Name || 
                                               o.name.toLowerCase().includes('size'));
      if (opt2) {
        option2Value = getCurrentOptionValue(2, opt2.value) || opt2.value;
      }
    }
    
    if (!option3Value && variant.selectedOptions && variant.selectedOptions.length > 0) {
      const opt3 = variant.selectedOptions.find(o => o.name === option3Name || 
                                               o.name.toLowerCase().includes('style') || 
                                               o.name.toLowerCase().includes('material'));
      if (opt3) {
        option3Value = getCurrentOptionValue(3, opt3.value) || opt3.value;
      }
    }
    
    console.log('%c[Variant Options]', 'color: #6600cc; font-weight: bold', {
      variantId: variant.id,
      option1Value,
      option2Value,
      option3Value
    });

    // Extract the base parts of the SKU
    let basePrefix = result.sku;
    
    // Remove any trailing dashes or spaces
    basePrefix = basePrefix.replace(/[-\s]+$/, '');
    
    // If the SKU already contains dashes, use the first two parts as the base
    if (basePrefix.includes('-')) {
      const skuParts = basePrefix.split('-');
      // Use up to 2 parts if available, otherwise use what we have
      basePrefix = skuParts.slice(0, Math.min(2, skuParts.length)).join('-');
    }
    
    // Create option-specific parts
    const optionParts = [];
    
    // Add option values to the SKU
    if (option1Value && option1Value !== 'Default Title') {
      // Use first 2-3 characters of option value, uppercase
      let code = '';
      if (option1Value.length <= 3) {
        code = option1Value.toUpperCase();
      } else {
        code = option1Value.substring(0, 3).toUpperCase();
      }
      optionParts.push(code);
    }
    
    if (option2Value) {
      // For size options, use as is if it's short (S, M, L, XL, etc.)
      if (option2Value.length <= 3) {
        optionParts.push(option2Value.toUpperCase());
      } else {
        // Otherwise use first 2 characters
        optionParts.push(option2Value.substring(0, 2).toUpperCase());
      }
    }
    
    if (option3Value) {
      // Use first 2 characters of option value, uppercase
      const code = option3Value.substring(0, 2).toUpperCase();
      optionParts.push(code);
    }
    
    // If no option parts were added (single variant product), add a "01" suffix
    if (optionParts.length === 0) {
      optionParts.push('01');
    }
    
    // Combine to create the variant SKU
    const variantSKU = optionParts.length > 0 ? 
      `${basePrefix}-${optionParts.join('-')}` : 
      basePrefix;
    
    console.log(`Generated variant SKU: ${variantSKU} for variant ${variant.id}`, {
      option1: option1Value,
      option2: option2Value,
      option3: option3Value
    });
    
    // Add to variant results
    variantResults[variant.id] = {
      sku: variantSKU
    };
  });

  // Add variant_skus to the result
  result.variant_skus = variantResults;
  
  return result;
};

/**
 * Parse a JSON result from OpenAI to handle common product fields
 * @param {Object} jsonResult - The parsed JSON result
 * @returns {Object} The processed result
 */
const processJsonResult = (jsonResult) => {
  // If it has metafields, return it directly
  if (jsonResult.metafields) {
    return jsonResult;
  }
  
  // If it doesn't have metafields but is an object, check for common product fields
  const commonProductFields = ['title', 'body_html', 'description', 'vendor', 'product_type', 'tags'];
  const result = {};
  
  // Check if any of the common product fields are in the response
  let hasTopLevelFields = false;
  for (const field of commonProductFields) {
    if (jsonResult[field] !== undefined) {
      result[field] = jsonResult[field];
      hasTopLevelFields = true;
    }
  }
  
  // If we found any top-level fields, return them directly
  if (hasTopLevelFields) {
    return result;
  }
  
  // Otherwise, wrap it in a metafields object as before
  return {
    metafields: jsonResult
  };
};

/**
 * Parse the OpenAI response into a usable result object
 * @param {Object} data - The OpenAI API response
 * @returns {Object} The parsed result
 */
export const parseTemplateResult = (data) => {
  try {
    // If the data already has an error property, return it directly
    if (data && data.error) {
      console.warn('Template result contains error:', data.error);
      return {
        error: data.error,
        metafields: {}
      };
    }
    
    // Check if the data is valid
    if (!data || !data.choices || !data.choices[0] || !data.choices[0].message) {
      console.error('Invalid OpenAI response data:', data);
      return {
        error: 'Invalid OpenAI response data',
        metafields: {}
      };
    }
    
    // Get the content from the message
    const content = data.choices[0].message.content;
    
    if (!content) {
      console.error('Empty content in OpenAI response:', data);
      return {
        error: 'Empty content in OpenAI response',
        rawContent: content,
        metafields: {}
      };
    }
    
    console.log('%c[Template Processing - Raw Content]', 'color: #9900cc; font-weight: bold');
    console.log(content);
    
    // Try to parse the content as JSON
    try {
      // First attempt: Try to parse the entire content as JSON
      const jsonResult = JSON.parse(content);
      console.log('%c[Template Processing - Parsed JSON (Direct)]', 'color: #9900cc; font-weight: bold', jsonResult);
      
      // Check if the result has a metafields property
      if (jsonResult && typeof jsonResult === 'object') {
        return processJsonResult(jsonResult);
      }
    } catch (e) {
      console.warn('Failed to parse content as direct JSON:', e.message);
    }
    
    // Second attempt: Try to extract JSON from the content
    try {
      // Look for JSON blocks in the content
      const jsonMatch = content.match(/```json\s*([\s\S]*?)\s*```/) || 
                       content.match(/```\s*([\s\S]*?)\s*```/) || 
                       content.match(/\{[\s\S]*\}/);
      
      if (jsonMatch && jsonMatch[1]) {
        const jsonContent = jsonMatch[1].trim();
        console.log('%c[Template Processing - Extracted JSON Block]', 'color: #9900cc; font-weight: bold', jsonContent);
        
        try {
          const jsonResult = JSON.parse(jsonContent);
          console.log('%c[Template Processing - Parsed JSON (Extracted)]', 'color: #9900cc; font-weight: bold', jsonResult);
          
          // Check if the result has a metafields property
          if (jsonResult && typeof jsonResult === 'object') {
            return processJsonResult(jsonResult);
          }
        } catch (e) {
          console.warn('Failed to parse extracted JSON block:', e.message);
        }
      }
    } catch (e) {
      console.warn('Failed to extract JSON from content:', e.message);
    }
    
    // Third attempt: Try to repair and parse the JSON
    try {
      // Use a JSON repair function to fix common JSON errors
      const repairedJson = repairJson(content);
      
      if (repairedJson) {
        console.log('%c[Template Processing - Repaired JSON]', 'color: #9900cc; font-weight: bold', repairedJson);
        
        try {
          const jsonResult = JSON.parse(repairedJson);
          console.log('%c[Template Processing - Parsed JSON (Repaired)]', 'color: #9900cc; font-weight: bold', jsonResult);
          
          // Check if the result has a metafields property
          if (jsonResult && typeof jsonResult === 'object') {
            return processJsonResult(jsonResult);
          }
        } catch (e) {
          console.warn('Failed to parse repaired JSON:', e.message);
        }
      }
    } catch (e) {
      console.warn('Failed to repair JSON:', e.message);
    }
    
    // Fourth attempt: Try to extract key-value pairs from the content
    try {
      const keyValuePairs = extractKeyValuePairs(content);
      
      if (keyValuePairs && Object.keys(keyValuePairs).length > 0) {
        console.log('%c[Template Processing - Extracted Key-Value Pairs]', 'color: #9900cc; font-weight: bold', keyValuePairs);
        
        return {
          metafields: keyValuePairs
        };
      }
    } catch (e) {
      console.warn('Failed to extract key-value pairs:', e.message);
    }
    
    // If all else fails, return the raw content
    console.warn('Failed to parse content as JSON, returning raw content');
    
    return {
      error: 'Failed to parse content as JSON',
      rawContent: content,
      metafields: {}
    };
  } catch (error) {
    console.error('Error parsing template result:', error);
    
    return {
      error: `Error parsing template result: ${error.message}`,
      metafields: {}
    };
  }
};

/**
 * Ensure that variant IDs in the result match the product variants
 * @param {Array} variants - The variants from the template result
 * @param {Object} product - The product with original variants
 * @returns {Array} The variants with corrected IDs
 */
export const ensureVariantIds = (variants, product) => {
  if (!variants || !Array.isArray(variants)) {
    console.warn('%c[ensureVariantIds] Invalid variants input', 'color: #cc3300; font-weight: bold', variants);
    return [];
  }
  
  if (!product || !product.variants || !Array.isArray(product.variants)) {
    console.warn('%c[ensureVariantIds] Invalid product or product variants', 'color: #cc3300; font-weight: bold', {
      hasProduct: !!product,
      hasVariants: !!(product && product.variants),
      isArray: !!(product && product.variants && Array.isArray(product.variants))
    });
    return variants;
  }
  
  console.log('%c[ensureVariantIds] Starting variant ID matching', 'color: #3366cc; font-weight: bold', {
    inputVariantCount: variants.length,
    productVariantCount: product.variants.length
  });
  
  // Map product variants by their position for faster lookup
  const productVariantsByPosition = {};
  const productVariantsById = {};
  
  product.variants.forEach((variant, index) => {
    if (variant.id) {
      // Store variant by ID (as string)
      productVariantsById[variant.id.toString()] = variant;
    }
    
    // Store variant by position
    productVariantsByPosition[index] = variant;
  });
  
  console.log('%c[ensureVariantIds] Product variants mapped', 'color: #3366cc; font-weight: bold', {
    byId: Object.keys(productVariantsById).length,
    byPosition: Object.keys(productVariantsByPosition).length
  });
  
  // Process each variant to ensure it has a valid ID
  return variants.map((variant, index) => {
    // Create a copy of the variant to avoid mutating the original
    const processedVariant = { ...variant };
    
    // Case 1: Variant already has a valid ID that matches a product variant
    if (variant.id && productVariantsById[variant.id.toString()]) {
      console.log('%c[ensureVariantIds] Variant already has valid ID', 'color: #339933; font-weight: bold', {
        variantIndex: index,
        variantId: variant.id.toString()
      });
      
      // Ensure ID is a string
      processedVariant.id = variant.id.toString();
      return processedVariant;
    }
    
    // Case 2: Try to match by position
    if (productVariantsByPosition[index]) {
      const matchedVariant = productVariantsByPosition[index];
      console.log('%c[ensureVariantIds] Matched variant by position', 'color: #339933; font-weight: bold', {
        variantIndex: index,
        matchedVariantId: matchedVariant.id.toString()
      });
      
      // Use the ID from the matched product variant
      processedVariant.id = matchedVariant.id.toString();
      return processedVariant;
    }
    
    // Case 3: Try to match by option values
    if (variant.option1 || variant.option2 || variant.option3) {
      const matchedVariant = product.variants.find(pv => 
        (variant.option1 === undefined || variant.option1 === pv.option1) &&
        (variant.option2 === undefined || variant.option2 === pv.option2) &&
        (variant.option3 === undefined || variant.option3 === pv.option3)
      );
      
      if (matchedVariant) {
        console.log('%c[ensureVariantIds] Matched variant by option values', 'color: #339933; font-weight: bold', {
          variantIndex: index,
          matchedVariantId: matchedVariant.id.toString(),
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3
        });
        
        // Use the ID from the matched product variant
        processedVariant.id = matchedVariant.id.toString();
        return processedVariant;
      }
    }
    
    // Case 4: No match found, use the first available product variant ID
    if (product.variants.length > 0) {
      console.warn('%c[ensureVariantIds] No match found, using first available variant ID', 'color: #cc6600; font-weight: bold', {
        variantIndex: index,
        fallbackVariantId: product.variants[0].id.toString()
      });
      
      // Use the ID from the first product variant
      processedVariant.id = product.variants[0].id.toString();
      return processedVariant;
    }
    
    // Case 5: No product variants available, generate a placeholder ID
    console.error('%c[ensureVariantIds] No product variants available, using placeholder ID', 'color: #cc0000; font-weight: bold', {
      variantIndex: index
    });
    
    // Use a placeholder ID
    processedVariant.id = `placeholder-${index}`;
    return processedVariant;
  });
};

/**
 * Extract option values from product data
 * @param {Object} product - The product object
 * @param {string} optionField - The option field name (e.g., 'option1_values')
 * @returns {Array} The option values
 */
const extractOptionValues = (product, optionField) => {
  try {
    // Determine which option number we're looking for (1, 2, or 3)
    const optionNumber = parseInt(optionField.charAt(optionField.indexOf('option') + 6), 10);
    
    // Check if product has options
    if (!product.options || !Array.isArray(product.options)) {
      console.log('%c[No Options Found]', 'color: #cc3300; font-weight: bold', {
        productId: product.id,
        optionNumber
      });
      return [];
    }
    
    // Find the option by position
    const option = product.options.find(opt => opt.position === optionNumber);
    if (!option) {
      console.log('%c[Option Not Found]', 'color: #cc3300; font-weight: bold', {
        productId: product.id,
        optionNumber,
        availableOptions: product.options.map(o => o.position)
      });
      return [];
    }
    
    // Return option values as array
    return option.values || [];
  } catch (error) {
    console.error('%c[Option Values Extraction Error]', 'color: #cc3300; font-weight: bold', {
      optionField,
      error: error.message
    });
    return [];
  }
};

/**
 * Extract option name from product data
 * @param {Object} product - The product object
 * @param {string} optionField - The option field name (e.g., 'option1_name')
 * @returns {string} The option name
 */
const extractOptionName = (product, optionField) => {
  try {
    // Determine which option number we're looking for (1, 2, or 3)
    const optionNumber = parseInt(optionField.charAt(optionField.indexOf('option') + 6), 10);
    
    // Check if product has options
    if (!product.options || !Array.isArray(product.options)) {
      console.log('%c[No Options Found]', 'color: #cc3300; font-weight: bold', {
        productId: product.id,
        optionNumber
      });
      return '';
    }
    
    // Find the option by position
    const option = product.options.find(opt => opt.position === optionNumber);
    if (!option) {
      console.log('%c[Option Not Found]', 'color: #cc3300; font-weight: bold', {
        productId: product.id,
        optionNumber,
        availableOptions: product.options.map(o => o.position)
      });
      return '';
    }
    
    // Return option name
    return option.name || '';
  } catch (error) {
    console.error('%c[Option Name Extraction Error]', 'color: #cc3300; font-weight: bold', {
      optionField,
      error: error.message
    });
    return '';
  }
};

/**
 * Prepare filtered product data with only the input fields
 * @param {Object} product - The product object
 * @param {Object} template - The template configuration
 * @param {Function} getFieldValue - Function to get field values
 * @returns {Object} The filtered product data
 */
export const prepareFilteredProductData = (product, template, getFieldValue) => {
  try {
    console.log('%c[Preparing Filtered Product Data]', 'color: #cc6633; font-weight: bold', {
      templateId: template.id,
      templateName: template.name,
      productId: product.id,
      inputFields: template.inputFields || [],
      inputMetafields: template.inputMetafields || []
    });
    
    // Use provided getFieldValue function or default to the one from templateUtils
    const fieldValueGetter = getFieldValue || defaultGetFieldValue;
    
    // Create a filtered product object with only the requested fields
    const filteredProduct = {};
    
    // Add product ID for reference
    filteredProduct.id = product.id;
    
    // Check for local edits in the UI
    let localEdits = {};
    if (typeof window !== 'undefined' && window.localEdits && product.id) {
      const productId = product.id.toString();
      if (window.localEdits[productId]) {
        localEdits = window.localEdits[productId];
        console.log('%c[Found Local Edits for Product]', 'color: #3366cc; font-weight: bold', {
          productId,
          editCount: Object.keys(localEdits).length
        });
      }
    }
    
    // Process input fields
    if (template.inputFields && Array.isArray(template.inputFields)) {
      template.inputFields.forEach(field => {
        try {
          let value;
          
          // Special handling for option values fields
          if (field === 'option1_values' || field === 'option2_values' || field === 'option3_values') {
            // Extract option number (1, 2, or 3)
            const optionNumber = parseInt(field.charAt(field.indexOf('option') + 6), 10);
            
            // Check for local edits first
            if (localEdits && localEdits[field] !== undefined) {
              value = localEdits[field];
              console.log('%c[Using Local Edit for Field]', 'color: #3366cc; font-weight: bold', {
                field,
                value
              });
            } else {
              // Get option values directly from product data
              if (product.options && Array.isArray(product.options)) {
                const option = product.options.find(opt => opt.position === optionNumber);
                value = option && option.values ? option.values : [];
                console.log('%c[Extracted Option Values Directly]', 'color: #3366cc; font-weight: bold', {
                  field,
                  optionNumber,
                  values: value
                });
              } else {
                value = [];
              }
            }
          } else {
            // Standard field handling
            if (localEdits && localEdits[field] !== undefined) {
              value = localEdits[field];
              console.log('%c[Using Local Edit for Field]', 'color: #3366cc; font-weight: bold', {
                field,
                value
              });
            } else {
              // If no local edit, use the field value getter
              value = fieldValueGetter(product, field);
            }
          }
          
          // Debug the value type and content
          const valueType = typeof value;
          const valueLength = valueType === 'string' ? value.length : null;
          const isArray = Array.isArray(value);
          const isObject = valueType === 'object' && !isArray && value !== null;
          
          console.log('%c[Processing Input Field]', 'color: #996633; font-weight: bold', {
            field,
            valueType,
            valueLength,
            isArray,
            isObject
          });
          
          // ALWAYS include selected input fields, even if they have no value
          // This ensures that every field the user selects is sent to the AI
          filteredProduct[field] = value !== undefined && value !== null ? value : '';
        } catch (error) {
          console.error('%c[Input Field Processing Error]', 'color: #cc3300; font-weight: bold', {
            field,
            error: error.message
          });
          // Continue with other fields
        }
      });
    }
    
    // Process input metafields
    const metafields = {};
    let metafieldCount = 0;
    
    if (template.inputMetafields && Array.isArray(template.inputMetafields)) {
      // Create a metafields object if it doesn't exist
      if (!filteredProduct.metafields) {
        filteredProduct.metafields = {};
      }
      
      template.inputMetafields.forEach(metafieldKey => {
        try {
          // Handle different metafield formats
          let namespace, key, value;
          
          // Check if metafieldKey is an object with namespace and key properties
          if (typeof metafieldKey === 'object' && metafieldKey !== null) {
            // Handle object format: { namespace: 'custom', key: 'googlekeywords' }
            namespace = metafieldKey.namespace;
            key = metafieldKey.key;
            
            console.log('%c[Processing Input Metafield (Object)]', 'color: #cc9933; font-weight: bold', {
              metafieldKey: `${namespace}.${key}`,
              namespace,
              key
            });
          } else if (typeof metafieldKey === 'string' && metafieldKey.includes('.')) {
            // Handle string format: 'namespace.key'
            [namespace, key] = metafieldKey.split('.');
            
            console.log('%c[Processing Input Metafield (String)]', 'color: #cc9933; font-weight: bold', {
              metafieldKey,
              namespace,
              key
            });
          } else {
            // Invalid format
            console.error('%c[Input Metafield Invalid Format]', 'color: #cc6600; font-weight: bold', {
              metafieldKey,
              type: typeof metafieldKey
            });
            return; // Skip this metafield
          }
          
          // Try to get the metafield value using the fieldValueGetter
          let metafieldValue;
          
          // First try with the metafield.namespace.key format
          metafieldValue = fieldValueGetter(product, `metafield.${namespace}.${key}`);
          
          // If not found, try with the metafields.namespace.key format
          if (metafieldValue === undefined || metafieldValue === '') {
            metafieldValue = fieldValueGetter(product, `metafields.${namespace}.${key}`);
          }
          
          // If still not found, try with the namespace.key format directly
          if (metafieldValue === undefined || metafieldValue === '') {
            metafieldValue = fieldValueGetter(product, `${namespace}.${key}`);
          }
          
          // Debug the value type and content
          const valueType = typeof metafieldValue;
          const valueLength = valueType === 'string' ? metafieldValue.length : null;
          const isArray = Array.isArray(metafieldValue);
          const isObject = valueType === 'object' && !isArray && metafieldValue !== null;
          
          console.log('%c[Processing Input Metafield Value]', 'color: #cc9933; font-weight: bold', {
            metafieldKey: `${namespace}.${key}`,
            namespace,
            key,
            valueType,
            valueLength,
            isArray,
            isObject
          });
          
          // ALWAYS include selected input metafields, even if they have no value
          // This ensures that every metafield the user selects is sent to the AI
          metafields[`${namespace}.${key}`] = metafieldValue !== undefined && metafieldValue !== null ? metafieldValue : '';
          metafieldCount++;
        } catch (error) {
          console.error('%c[Input Metafield Processing Error]', 'color: #cc6600; font-weight: bold', {
            metafieldKey: typeof metafieldKey === 'object' ? 
              `${metafieldKey?.namespace || 'unknown'}.${metafieldKey?.key || 'unknown'}` : 
              String(metafieldKey),
            error: error.message
          });
          // Continue with other metafields
        }
      });
    }
    
    // Add metafields to the filtered product
    if (metafieldCount > 0) {
      filteredProduct.metafields = metafields;
    } else {
      // Always include an empty metafields object for consistency
      filteredProduct.metafields = {};
    }
    
    // Add inputMetafields array to the filtered product for direct access
    if (template.inputMetafields && Array.isArray(template.inputMetafields) && template.inputMetafields.length > 0) {
      filteredProduct.inputMetafields = template.inputMetafields.map(metafieldKey => {
        const { namespace, key } = parseMetafieldPath(typeof metafieldKey === 'string' ? metafieldKey : `${metafieldKey.namespace}.${metafieldKey.key}`);
        return { namespace, key };
      });
    }
    
    // Process option values
    if (template.inputFields && Array.isArray(template.inputFields)) {
      template.inputFields.forEach(field => {
        if (field.includes('option') && field.endsWith('_values')) {
          try {
            const optionValues = extractOptionValues(product, field);
            
            // Debug the value type and content
            const valueType = typeof optionValues;
            const valueLength = valueType === 'string' ? optionValues.length : null;
            const isArray = Array.isArray(optionValues);
            const isObject = valueType === 'object' && !isArray && optionValues !== null;
            
            console.log('%c[Processing Option Values]', 'color: #996633; font-weight: bold', {
              field,
              valueType,
              valueLength,
              isArray,
              isObject
            });
            
            // ALWAYS include selected input fields, even if they have no value
            // This ensures that every field the user selects is sent to the AI
            filteredProduct[field] = optionValues !== undefined && optionValues !== null ? optionValues : [];
          } catch (error) {
            console.error('%c[Option Values Processing Error]', 'color: #cc3300; font-weight: bold', {
              field,
              error: error.message
            });
            // Continue with other fields
          }
        } else if (field.includes('option') && field.endsWith('_name')) {
          try {
            const optionName = extractOptionName(product, field);
            
            // Debug the value type and content
            const valueType = typeof optionName;
            const valueLength = valueType === 'string' ? optionName.length : null;
            const isArray = Array.isArray(optionName);
            const isObject = valueType === 'object' && !isArray && optionName !== null;
            
            console.log('%c[Processing Option Name]', 'color: #996633; font-weight: bold', {
              field,
              valueType,
              valueLength,
              isArray,
              isObject
            });
            
            // ALWAYS include selected input fields, even if they have no value
            // This ensures that every field the user selects is sent to the AI
            filteredProduct[field] = optionName !== undefined && optionName !== null ? optionName : '';
          } catch (error) {
            console.error('%c[Option Name Processing Error]', 'color: #cc3300; font-weight: bold', {
              field,
              error: error.message
            });
            // Continue with other fields
          }
        }
      });
    }
    
    // === AUTO-INJECT VARIANTS FOR SKU GENERATION ===
    // If the template outputs to variant fields (like variants.sku) but the user didn't explicitly 
    // select 'variants' as an input, we MUST inject the variant data so the AI knows the IDs and options!
    const outputsToVariants = template.outputFields && template.outputFields.some(f => f.startsWith('variants.'));
    const inputsHasVariants = template.inputFields && (template.inputFields.some(f => f === 'variants' || f.startsWith('variants.')));
    
    if (outputsToVariants && !inputsHasVariants && product.variants && Array.isArray(product.variants) && product.variants.length > 0) {
      filteredProduct.variants = product.variants.map(v => {
        // Build a simplified variant object
        const simpleVariant = { id: v.id };
        if (v.title && v.title !== 'Default Title') simpleVariant.title = v.title;
        if (v.option1) simpleVariant.option1 = v.option1;
        if (v.option2) simpleVariant.option2 = v.option2;
        if (v.option3) simpleVariant.option3 = v.option3;
        if (v.sku) simpleVariant.sku = v.sku;
        
        // Check for local edit overrides
        if (localEdits && localEdits.variants && localEdits.variants[v.id] && localEdits.variants[v.id].sku !== undefined) {
           simpleVariant.sku = localEdits.variants[v.id].sku;
        }
        
        return simpleVariant;
      });
      console.log('%c[Auto-injected Variants for Output Mapping]', 'color: #33cc66; font-weight: bold', {
        variantCount: filteredProduct.variants.length
      });
    }
    
    console.log('%c[Filtered Product Data Complete]', 'color: #996600; font-weight: bold', {
      fieldCount: Object.keys(filteredProduct).length - 2, // Subtract id and metafields
      metafieldCount
    });
    
    return filteredProduct;
  } catch (error) {
    console.error('%c[Error Preparing Filtered Product Data]', 'color: #cc3300; font-weight: bold', {
      error: error.message,
      stack: error.stack
    });
    
    // Return a minimal product object with just the ID
    return { id: product.id, metafields: {} };
  }
};

/**
 * Generate a template prompt for OpenAI
 * @param {Object} template - The template configuration
 * @param {Object} productData - The filtered product data
 * @returns {Object} The messages array for OpenAI
 */
export const generateTemplatePrompt = (template, productData) => {
  try {
    // Log the raw template and product data
    console.log('%c[Raw Template Data]', 'color: #ff6600; font-weight: bold; font-size: 14px');
    console.log(JSON.stringify(template, null, 2));
    
    console.log('%c[Raw Product Data]', 'color: #ff6600; font-weight: bold; font-size: 14px');
    console.log(JSON.stringify(productData, null, 2));
    
    console.log('%c[Generating Template Prompt]', 'color: #3366cc; font-weight: bold', {
      templateId: template.id,
      templateName: template.name,
      outputFields: template.outputFields || [],
      outputMetafields: template.outputMetafields || [],
      systemPromptLength: getSystemPromptForFields(template.outputFields || [], template.outputMetafields || []).length
    });
    
    // Ensure outputFields is an array
    const outputFields = template.outputFields || [];
    
    // Get the system prompt based on output fields
    const systemPrompt = getSystemPromptForFields(outputFields, template.outputMetafields || []);
    
    // Log the generated system prompt for debugging
    console.log('%c[Generated System Prompt]', 'color: #9932cc; font-weight: bold');
    console.log(systemPrompt);
    
    // If this is a variant template, ensure we include the actual variant IDs in the product data
    let enhancedProductData = productData;
    if (template.type === 'variant' && productData.variants) {
      // Make sure we're not modifying the original productData
      enhancedProductData = { ...productData };
      
      // If variants are not included in the product data but exist in the original product
      if (!enhancedProductData.variants && template.product && template.product.variants) {
        enhancedProductData.variants = template.product.variants.map(variant => ({
          id: variant.id,
          option1: variant.option1,
          option2: variant.option2,
          option3: variant.option3
        }));
        
        console.log('%c[Added Variant IDs to Product Data]', 'color: #669933; font-weight: bold', {
          variantCount: enhancedProductData.variants.length
        });
      }
    }
    
    // Create the messages array
    const messages = [
      {
        role: 'system',
        content: systemPrompt
      },
      {
        role: 'user',
        content: template.prompt
      }
    ];
    
    // Add product data to the user message if there are input fields
    if (Object.keys(enhancedProductData).length > 0) {
      // Format product data as a clean string
      let productDataString = '\n\n### PRODUCT DATA ###\n';
      
      // Add each field from the product data
      Object.keys(enhancedProductData).forEach(key => {
        if (key !== 'metafields' && key !== 'id') {
          const value = enhancedProductData[key];
          if (typeof value === 'string') {
            productDataString += `\n${key}: ${value}`;
          } else if (value !== undefined) {
            // For non-string values, try to stringify them
            try {
              productDataString += `\n${key}: ${JSON.stringify(value)}`;
            } catch (e) {
              productDataString += `\n${key}: [Complex data]`;
            }
          }
        }
      });
      
      // Add original option values context to preserve order
      if (enhancedProductData.options && Array.isArray(enhancedProductData.options)) {
        productDataString += '\n\n### ORIGINAL OPTION VALUES (PRESERVE EXACT ORDER) ###';
        enhancedProductData.options.forEach((option, index) => {
          if (option && option.values && Array.isArray(option.values)) {
            productDataString += `\noption${index + 1}_name: ${option.name || `Option ${index + 1}`}`;
            productDataString += `\noption${index + 1}_values: ${option.values.join(', ')}`;
          }
        });
        productDataString += '\n\nIMPORTANT: When translating/optimizing option values, maintain the EXACT SAME ORDER as shown above to preserve image associations.';
      }
      
      // Add metafields if they exist
      if (enhancedProductData.metafields && Object.keys(enhancedProductData.metafields).length > 0) {
        productDataString += '\n\n### METAFIELDS ###';
        Object.keys(enhancedProductData.metafields).forEach(key => {
          productDataString += `\n${key}: ${enhancedProductData.metafields[key]}`;
        });
      }
      
      // Append the product data to the user message
      messages[1].content += productDataString;
      
      // === IMAGE PLACEHOLDER HINT ===
      // If the template has image placeholders enabled, append a hint to the prompt
      // telling the AI how many images the product has and how to use {{IMAGE_N}} placeholders
      if (template.enableImagePlaceholders) {
        // Count images from the enhanced product data or the original product
        let imageCount = 0;
        if (enhancedProductData.images) {
          if (Array.isArray(enhancedProductData.images)) {
            imageCount = enhancedProductData.images.length;
          } else if (enhancedProductData.images.edges) {
            imageCount = enhancedProductData.images.edges.length;
          }
        }
        // Fallback: check the raw product from the template context
        if (imageCount === 0 && template._productImageCount) {
          imageCount = template._productImageCount;
        }
        
        const mode = template.imagePlaceholderMode || 'manual';
        const imageAnalyses = template._imageAnalyses || null;
        const hint = generatePromptHint(imageCount, mode, imageAnalyses);
        
        messages[1].content += hint;
        
        console.log('%c[Image Placeholder Hint Added]', 'color: #e67e22; font-weight: bold', {
          imageCount,
          mode,
          hintLength: hint.length,
          hasAnalyses: !!imageAnalyses
        });
      } else if (template._imageAnalyses && template._imageAnalyses.length > 0) {
        // === IMAGE ANALYSIS CONTEXT (no placeholders) ===
        // Even without image placeholders, inject the image analysis data into the prompt
        // so the description AI can use it for accurate color, material, variant descriptions
        const analysisContext = template._imageAnalyses.map((analysis, i) => {
          const idx = analysis.imageIndex || (i + 1);
          const cat = analysis.category ? ` [${analysis.category}]` : '';
          const desc = analysis.description || '';
          const alt = analysis.suggestedAltText || '';
          return `- Image ${idx}${cat}: ${desc}${alt ? ` (Alt: "${alt}")` : ''}`;
        }).join('\n');
        
        const variantImages = template._imageAnalyses.filter(a => a.category === 'variant');
        const variantNote = variantImages.length > 0 
          ? `\n\nNote: ${variantImages.length} images show different color/style variants. Use this to identify and describe available product variants.`
          : '';
        
        messages[1].content += `\n\n## Product Image Analysis (from Vision AI)\n` +
          `The following ${template._imageAnalyses.length} product images were analyzed. ` +
          `Use these descriptions to write a more accurate product description with correct colors, materials, features, and variants.\n\n` +
          `${analysisContext}${variantNote}`;
        
        console.log('%c[Image Analysis Context Added (no placeholders)]', 'color: #27ae60; font-weight: bold', {
          analysisCount: template._imageAnalyses.length,
          variantCount: variantImages.length
        });
      }
      
      // Log the complete prompt for debugging
      console.log('%c[Complete Prompt with Product Data]', 'color: #3366cc; font-weight: bold', {
        promptLength: messages[1].content.length,
        includesProductData: true,
        fieldCount: Object.keys(enhancedProductData).filter(k => k !== 'metafields' && k !== 'id').length,
        metafieldCount: enhancedProductData.metafields ? Object.keys(enhancedProductData.metafields).length : 0
      });
    }
    
    // Add output field instructions if there are any output fields or metafields
    if ((outputFields && outputFields.length > 0) || (template.outputMetafields && template.outputMetafields.length > 0)) {
      // Add a note about the expected output format
      let outputInstructions = '\n\nIMPORTANT: Please respond with a valid JSON object using EXACTLY the following field names:';
      
      // Add regular output fields with their exact Shopify field names
      if (outputFields && outputFields.length > 0) {
        outputFields.forEach(field => {
          // Map field names to their exact Shopify API field names
          const shopifyFieldName = getShopifyFieldName(field);
          outputInstructions += `\n- "${shopifyFieldName}": content for ${field}`;
        });
      }
      
      // Add output metafields with their full keys
      if (template.outputMetafields && template.outputMetafields.length > 0) {
        outputInstructions += '\n\nFor metafields, you MUST use the EXACT field name format shown below in your JSON response:';
        
        template.outputMetafields.forEach(metafield => {
          if (typeof metafield === 'object' && metafield.namespace && metafield.key) {
            const fullKey = `${metafield.namespace}.${metafield.key}`;
            outputInstructions += `\n- "${fullKey}": content for ${metafield.key}`;
          } else if (typeof metafield === 'string' && metafield.includes('.')) {
            const [namespace, key] = metafield.split('.');
            outputInstructions += `\n- "${metafield}": content for ${key}`;
          }
        });
      }
      
      // Add examples of correct response format for common fields
      outputInstructions += '\n\nExamples of correct field names:';
      if (outputFields.includes('title')) {
        outputInstructions += '\n- "title": "Your product title here"';
      }
      if (outputFields.includes('description')) {
        outputInstructions += '\n- "body_html": "<p>Your product description here</p>"';
      }
      if (outputFields.includes('tags')) {
        outputInstructions += '\n- "tags": "tag1, tag2, tag3"';
      }
      
      // Add the output instructions to the user message
      messages[1].content += outputInstructions;
    }
    
    return messages;
  } catch (error) {
    console.error('%c[Template Prompt Generation Error]', 'color: #cc3300; font-weight: bold', {
      error: error.message
    });
    throw new Error(`Error generating template prompt: ${error.message}`);
  }
};

/**
 * Get the exact Shopify field name for a given field
 * @param {string} field - The field name
 * @returns {string} The exact Shopify field name
 */
const getShopifyFieldName = (field) => {
  const shopifyFieldNames = {
    // Basic product fields
    title: 'title',
    description: 'body_html',
    vendor: 'vendor',
    product_type: 'product_type',
    producttype: 'product_type',  // Add lowercase variation
    'product-type': 'product_type', // Add hyphenated variation
    handle: 'handle',
    tags: 'tags',
    status: 'status',
    
    // SEO fields
    seo_title: 'metafields.seo.title',
    seo_description: 'metafields.seo.description',
    
    // Option fields
    option1: 'option1',
    option2: 'option2',
    option3: 'option3',
    option1_name: 'option1_name',
    option2_name: 'option2_name',
    option3_name: 'option3_name',
    option1_values: 'option1_values',
    option2_values: 'option2_values',
    option3_values: 'option3_values',
    
    // Variant fields
    variants: 'variants',
    'variants.sku': 'variants.sku',
    'variants.price': 'variants.price',
    'variants.compare_at_price': 'variants.compare_at_price',
    'variants.inventory_quantity': 'variants.inventory_quantity',
    'variants.barcode': 'variants.barcode',
    'variants.weight': 'variants.weight',
    'variants.weight_unit': 'variants.weight_unit',
    
    // Image fields
    images: 'images',
    featured_image: 'featured_image',
    alt_text: 'alt_text',
    image_alt_text: 'alt_text',
    image_alt: 'alt_text',
  };
  
  return shopifyFieldNames[field] || field;
};

/**
 * Repair common JSON syntax errors
 * @param {string} jsonString - The potentially invalid JSON string
 * @returns {string} The repaired JSON string
 */
const repairJson = (jsonString) => {
  try {
    if (!jsonString) return null;
    
    let repairedJson = jsonString;
    
    // 1. Extract JSON from the string if it's wrapped in text
    const jsonMatch = repairedJson.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      repairedJson = jsonMatch[0];
    }
    
    // 2. Fix missing commas between array elements
    repairedJson = repairedJson.replace(/}(\s*){/g, '},\n$1{');
    
    // 3. Fix missing commas between object properties
    repairedJson = repairedJson.replace(/"([^"]+)"\s*:\s*("[^"]*"|[\d.]+|\{[^}]*\}|\[[^\]]*\]|true|false|null)(\s+)"([^"]+)"/g, '"$1": $2,\n$3"$4"');
    
    // 4. Replace single quotes with double quotes (common in AI responses)
    repairedJson = repairedJson.replace(/'/g, '"');
    
    // 5. Fix trailing commas in arrays and objects
    repairedJson = repairedJson.replace(/,(\s*[\]}])/g, '$1');
    
    // 6. Fix missing quotes around property names
    repairedJson = repairedJson.replace(/([{,]\s*)([a-zA-Z0-9_$]+)(\s*:)/g, '$1"$2"$3');
    
    // 7. Fix unescaped quotes in strings
    repairedJson = repairedJson.replace(/"([^"]*)"([^"]*)"([^"]*)"/g, '"$1\\"$2\\"$3"');
    
    // 8. Fix specific issue with variants.sku format in the system prompt
    repairedJson = repairedJson.replace(/{\s*"id":\s*"[^"]+",\s*sku:\s*"([^"]+)"\s*}/g, '{"id": "$1", "sku": "$2"}');
    
    return repairedJson;
  } catch (error) {
    console.error('Error repairing JSON:', error);
    return null;
  }
};

/**
 * Extract key-value pairs from text content
 * @param {string} content - The text content to extract from
 * @returns {Object} The extracted key-value pairs
 */
const extractKeyValuePairs = (content) => {
  try {
    if (!content) return {};
    
    const keyValuePairs = {};
    
    // Look for patterns like "key": "value" or key: value
    const keyValueRegex = /"?([a-zA-Z0-9_]+)"?\s*:\s*"?([^",\{\}\[\]]+)"?/g;
    let match;
    
    while ((match = keyValueRegex.exec(content)) !== null) {
      const key = match[1].trim();
      const value = match[2].trim().replace(/^["']|["']$/g, ''); // Remove quotes if present
      
      if (key && value) {
        keyValuePairs[key] = value;
      }
    }
    
    return keyValuePairs;
  } catch (error) {
    console.error('Error extracting key-value pairs:', error);
    return {};
  }
};
